import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Calendar, Clock, Send, CheckCircle, User, Briefcase } from "lucide-react";
import WeeklyAvailabilityGrid from "./WeeklyAvailabilityGrid";
import TimeSlotDetailModal from "./TimeSlotDetailModal";
import DynamicList from "../../../components/dynamic/List";
import Modal from "../../../components/ui/Modal";
import { useModal } from "../../../hooks/useModal";
import { ApiService } from "../../../services/api";
import { AppointmentProviderItem, AvailableSlotsWeekResponse, AppointmentCreatePayload } from "../../../types/appointment";
import checkPermission from "../../../utils/permission";

interface TimeSlot {
  id: string;
  period: string;
  startTime: string;
  endTime: string;
}

interface ProviderUiItem {
  id: string;
  name: string;
  title: string;
  department: string;
  email: string;
  avatar?: string;
}

interface Availability {
  id: string;
  teacherId: string;
  day: string;
  timeSlotId: string;
  isAvailable: boolean;
}

interface SubTimeSlotAvailability {
  id: string;
  teacherId: string;
  day: string;
  timeSlotId: string;
  subTime: string;
  isAvailable: boolean;
  ended?: string;
}

const generateSubTimeSlots = (startTime: string): string[] => {
  const [hour, minute] = startTime.split(":").map(Number);
  const subSlots: string[] = [];
  for (let i = 0; i < 60; i += 10) {
    const mins = (minute + i) % 60;
    const hrs = hour + Math.floor((minute + i) / 60);
    subSlots.push(`${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`);
  }

  return subSlots;
};

const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

const findParentTimeSlotId = (started: string, blocks: TimeSlot[]): string => {
  const startedMin = toMinutes(started);
  const found = blocks.find((ts) => {
    const s = toMinutes(ts.startTime);
    const e = toMinutes(ts.endTime);
    return startedMin >= s && startedMin < e;
  });
  return found ? String(found.id) : started;
};

const mockTimeSlots: TimeSlot[] = [
  { id: "1", period: "1. Saat", startTime: "08:00", endTime: "09:00" },
  { id: "2", period: "2. Saat", startTime: "09:00", endTime: "10:00" },
  { id: "3", period: "3. Saat", startTime: "10:00", endTime: "11:00" },
  { id: "4", period: "4. Saat", startTime: "11:00", endTime: "12:00" },
  { id: "5", period: "5. Saat", startTime: "12:00", endTime: "13:00" },
  { id: "6", period: "6. Saat", startTime: "13:00", endTime: "14:00" },
  { id: "7", period: "7. Saat", startTime: "14:00", endTime: "15:00" },
  { id: "8", period: "8. Saat", startTime: "15:00", endTime: "16:00" },
  { id: "9", period: "9. Saat", startTime: "16:00", endTime: "17:00" },
  { id: "10", period: "10. Saat", startTime: "17:00", endTime: "18:00" },
  { id: "11", period: "11. Saat", startTime: "18:00", endTime: "19:00" },
  { id: "12", period: "12. Saat", startTime: "19:00", endTime: "20:00" },
];

const days = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

function toProviderUi(item: AppointmentProviderItem): ProviderUiItem {
  const name = `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim();
  return {
    id: String(item.id),
    name: name || item.email || "Sağlayıcı",
    title: item.department?.name || "",
    department: item.department?.name || "",
    email: item.email || "",
    avatar: item.media?.url,
  };
}

const mockAvailability: Availability[] = [
  {
    id: "1",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "1",
    isAvailable: true,
  },
  {
    id: "2",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "2",
    isAvailable: true,
  },
  {
    id: "3",
    teacherId: "teacher-1",
    day: "Salı",
    timeSlotId: "3",
    isAvailable: true,
  },
  {
    id: "4",
    teacherId: "teacher-1",
    day: "Çarşamba",
    timeSlotId: "5",
    isAvailable: true,
  },
  {
    id: "5",
    teacherId: "teacher-2",
    day: "Pazartesi",
    timeSlotId: "2",
    isAvailable: true,
  },
  {
    id: "6",
    teacherId: "teacher-2",
    day: "Salı",
    timeSlotId: "4",
    isAvailable: true,
  },
  {
    id: "7",
    teacherId: "teacher-3",
    day: "Çarşamba",
    timeSlotId: "1",
    isAvailable: true,
  },
  {
    id: "8",
    teacherId: "doctor-1",
    day: "Pazartesi",
    timeSlotId: "3",
    isAvailable: true,
  },
  {
    id: "9",
    teacherId: "doctor-1",
    day: "Pazartesi",
    timeSlotId: "4",
    isAvailable: true,
  },
  {
    id: "10",
    teacherId: "doctor-1",
    day: "Çarşamba",
    timeSlotId: "2",
    isAvailable: true,
  },
  {
    id: "11",
    teacherId: "doctor-1",
    day: "Perşembe",
    timeSlotId: "5",
    isAvailable: true,
  },
  {
    id: "12",
    teacherId: "hr-1",
    day: "Salı",
    timeSlotId: "2",
    isAvailable: true,
  },
  {
    id: "13",
    teacherId: "hr-1",
    day: "Salı",
    timeSlotId: "3",
    isAvailable: true,
  },
  {
    id: "14",
    teacherId: "hr-1",
    day: "Perşembe",
    timeSlotId: "1",
    isAvailable: true,
  },
  {
    id: "15",
    teacherId: "hr-1",
    day: "Cuma",
    timeSlotId: "6",
    isAvailable: true,
  },
  {
    id: "16",
    teacherId: "tech-1",
    day: "Pazartesi",
    timeSlotId: "5",
    isAvailable: true,
  },
  {
    id: "17",
    teacherId: "tech-1",
    day: "Salı",
    timeSlotId: "1",
    isAvailable: true,
  },
  {
    id: "18",
    teacherId: "tech-1",
    day: "Çarşamba",
    timeSlotId: "6",
    isAvailable: true,
  },
  {
    id: "19",
    teacherId: "tech-1",
    day: "Cuma",
    timeSlotId: "2",
    isAvailable: true,
  },
  {
    id: "20",
    teacherId: "support-1",
    day: "Pazartesi",
    timeSlotId: "1",
    isAvailable: true,
  },
  {
    id: "21",
    teacherId: "support-1",
    day: "Salı",
    timeSlotId: "5",
    isAvailable: true,
  },
  {
    id: "22",
    teacherId: "support-1",
    day: "Çarşamba",
    timeSlotId: "3",
    isAvailable: true,
  },
  {
    id: "23",
    teacherId: "support-1",
    day: "Perşembe",
    timeSlotId: "4",
    isAvailable: true,
  },
  {
    id: "24",
    teacherId: "support-1",
    day: "Cuma",
    timeSlotId: "7",
    isAvailable: true,
  },
];

const mockSubTimeSlotAvailability: SubTimeSlotAvailability[] = [
  {
    id: "sub-1",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "1",
    subTime: "08:10",
    isAvailable: true,
  },
  {
    id: "sub-2",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "1",
    subTime: "08:20",
    isAvailable: true,
  },
  {
    id: "sub-3",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "1",
    subTime: "08:30",
    isAvailable: false,
  },
  {
    id: "sub-4",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "1",
    subTime: "08:40",
    isAvailable: true,
  },
  {
    id: "sub-5",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "1",
    subTime: "08:50",
    isAvailable: true,
  },
  {
    id: "sub-6",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "2",
    subTime: "09:10",
    isAvailable: true,
  },
  {
    id: "sub-7",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "2",
    subTime: "09:20",
    isAvailable: true,
  },
  {
    id: "sub-8",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "2",
    subTime: "09:30",
    isAvailable: true,
  },
  {
    id: "sub-9",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "2",
    subTime: "09:40",
    isAvailable: true,
  },
  {
    id: "sub-10",
    teacherId: "teacher-1",
    day: "Pazartesi",
    timeSlotId: "2",
    subTime: "09:50",
    isAvailable: true,
  },
];

export default function ClientViewSelect() {
  const { showAlert } = useModal();
  const [selectedProvider, setSelectedProvider] = useState<ProviderUiItem | null>(null);
  const [subTimeSlotAvailability, setSubTimeSlotAvailability] = useState<SubTimeSlotAvailability[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<{
    day: string;
    timeSlotId: string;
    subTime?: string;
    ended?: string;
  } | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  const canAddAppointment = checkPermission("appointment_add");

  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [selectedTimeSlotForModal, setSelectedTimeSlotForModal] = useState<TimeSlot | null>(null);
  const [selectedDayForModal, setSelectedDayForModal] = useState<string>("");

  const fetchProviders = async (page: number, searchTerm?: string) => {
    const res = await ApiService.getAppointmentProviders({
      limit: 50,
      page,
      search: searchTerm || undefined,
    });

    const anyRes: any = res as any;
    const meta = anyRes?.meta || {};
    const pageNum = meta.page ?? anyRes.page ?? page ?? 1;
    const limitNum = meta.limit ?? anyRes.limit ?? 50;
    const totalItemsNum = meta.totalItems ?? anyRes.totalItems ?? (Array.isArray(anyRes?.data) ? anyRes.data.length : 0);
    const totalPagesNum = meta.totalPages ?? anyRes.totalPages ?? Math.max(1, Math.ceil((totalItemsNum || 0) / (limitNum || 1)));

    return {
      data: anyRes?.data ?? [],
      meta: {
        page: pageNum,
        totalPages: totalPagesNum,
        totalItems: totalItemsNum,
        limit: limitNum,
      },
    };
  };

  const renderProviderItem = (provider: AppointmentProviderItem) => {
    const providerUi = toProviderUi(provider);

    const handleProviderClick = () => {
      if (!canAddAppointment) {
        showAlert({
          title: "Yetki Gerekli",
          message: "Randevu almak için yetkiniz bulunmuyor.",
          type: "error",
        });
        return;
      }
      setSelectedProvider(providerUi);
      setShowCalendarModal(true);
    };

    return (
      <div
        key={providerUi.id}
        onClick={handleProviderClick}
        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 cursor-pointer mb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden">
              {providerUi.avatar ? (
                <img src={providerUi.avatar} alt={providerUi.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-sm font-medium">
                  {providerUi.name
                    .split(" ")
                    .map((n) => n.charAt(0))
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-gray-900 font-semibold text-base truncate">{providerUi.name}</h3>
              <p className="text-gray-600 text-sm truncate">{providerUi.department}</p>
            </div>
          </div>
          <button className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Randevu Al
          </button>
        </div>
      </div>
    );
  };

  const [dayToDateMap, setDayToDateMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadSlots = async () => {
      if (!selectedProvider) return;
      const wres = (await ApiService.getAvailableSlots({
        providerId: selectedProvider.id,
        week: new Date().toISOString().split("T")[0],
      })) as AvailableSlotsWeekResponse | any;
      const weekSlots = (wres as any)?.weekSlots || {};
      const mapped: SubTimeSlotAvailability[] = [];
      const dayNameMap: Record<string, string> = {
        monday: "Pazartesi",
        tuesday: "Salı",
        wednesday: "Çarşamba",
        thursday: "Perşembe",
        friday: "Cuma",
        saturday: "Cumartesi",
        sunday: "Pazar",
      };
      const dayToDate: Record<string, string> = {};
      Object.entries(weekSlots).forEach(([engDay, info]: any) => {
        const dayTr = dayNameMap[engDay] || engDay;
        if (info?.date) dayToDate[dayTr] = info.date;
        (info?.slots || []).forEach((slot: any) => {
          mapped.push({
            id: `${engDay}-${slot.started}`,
            teacherId: selectedProvider.id,
            day: dayTr,
            timeSlotId: findParentTimeSlotId(slot.started, mockTimeSlots),
            subTime: slot.started,
            ended: slot.ended,
            isAvailable: !!slot.isAvailable,
          });
        });
      });
      setSubTimeSlotAvailability(mapped);
      setDayToDateMap(dayToDate);
    };
    loadSlots();
  }, [selectedProvider]);

  const checkFullAvailability = useCallback(
    (day: string, timeSlotId: string): boolean => {
      if (!selectedProvider) return false;

      const subSlots = subTimeSlotAvailability.filter(
        (s) => s.teacherId === selectedProvider.id && s.day === day && s.timeSlotId === timeSlotId
      );

      if (subSlots.length === 0) return false;

      return subSlots.every((s) => s.isAvailable);
    },
    [selectedProvider, subTimeSlotAvailability]
  );

  const checkPartialAvailability = useCallback(
    (day: string, timeSlotId: string): boolean => {
      if (!selectedProvider) return false;

      const subSlots = subTimeSlotAvailability.filter(
        (s) => s.teacherId === selectedProvider.id && s.day === day && s.timeSlotId === timeSlotId
      );

      if (subSlots.length === 0) return false;

      const availableCount = subSlots.filter((s) => s.isAvailable).length;
      const totalCount = subSlots.length;

      return availableCount > 0 && availableCount < totalCount;
    },
    [selectedProvider, subTimeSlotAvailability]
  );

  const computedAvailability: Availability[] = useMemo(() => {
    if (!selectedProvider) return [];

    return days.flatMap((day) =>
      mockTimeSlots.map((slot) => {
        const hasAnyAvailability = subTimeSlotAvailability.some(
          (s) => s.teacherId === selectedProvider.id && s.day === day && s.timeSlotId === slot.id && s.isAvailable
        );

        return {
          id: `${day}-${slot.id}`,
          teacherId: selectedProvider.id,
          day,
          timeSlotId: slot.id,
          isAvailable: hasAnyAvailability,
        };
      })
    );
  }, [selectedProvider, subTimeSlotAvailability]);

  const getSubTimeSlotsForModal = () => {
    if (!selectedTimeSlotForModal || !selectedProvider) return [];

    const subTimes = generateSubTimeSlots(selectedTimeSlotForModal.startTime);

    return subTimes.map((time) => {
      const existing = subTimeSlotAvailability.find(
        (s) =>
          s.teacherId === selectedProvider.id &&
          s.day === selectedDayForModal &&
          s.timeSlotId === selectedTimeSlotForModal.id &&
          s.subTime === time
      );

      return {
        time,
        isAvailable: existing ? existing.isAvailable : false,
      };
    });
  };

  const handleSubSlotSelect = (time: string) => {
    if (!selectedProvider || !selectedTimeSlotForModal) return;

    const subSlot = subTimeSlotAvailability.find(
      (s) =>
        s.teacherId === selectedProvider.id &&
        s.day === selectedDayForModal &&
        s.timeSlotId === selectedTimeSlotForModal.id &&
        s.subTime === time &&
        s.isAvailable
    );

    if (subSlot) {
      setSelectedSlot({
        day: selectedDayForModal,
        timeSlotId: selectedTimeSlotForModal.id,
        subTime: time,
        ended: subSlot.ended,
      });
      setShowTimeSlotModal(false);
      setShowBookingModal(true);
    }
  };

  const handleSlotClick = (day: string, timeSlotId: string) => {
    if (!selectedProvider) return;

    const hasAnyAvailableSubSlot = subTimeSlotAvailability.some(
      (s) => s.teacherId === selectedProvider.id && s.day === day && s.timeSlotId === timeSlotId && s.isAvailable
    );

    if (hasAnyAvailableSubSlot) {
      const slot = mockTimeSlots.find((s) => s.id === timeSlotId);
      setSelectedTimeSlotForModal(slot || null);
      setSelectedDayForModal(day);
      setShowTimeSlotModal(true);
    }
  };

  const handleBookAppointment = async () => {
    if (!selectedProvider || !selectedSlot) return;
    const dateStr = dayToDateMap[selectedSlot.day] || new Date().toISOString().split("T")[0];
    const slot = mockTimeSlots.find((s) => s.id === selectedSlot.timeSlotId);
    const payload: AppointmentCreatePayload = {
      providerId: selectedProvider.id,
      description: "",
      date: dateStr,
      times: [
        {
          started: selectedSlot.subTime || slot?.startTime || "",
          ended:
            selectedSlot.ended ||
            (selectedSlot.subTime
              ? (() => {
                  const [h, m] = (selectedSlot.subTime as string).split(":").map(Number);
                  const d = new Date(2000, 0, 1, h, m, 0, 0);
                  d.setMinutes(d.getMinutes() + 10);
                  const hh = d.getHours().toString().padStart(2, "0");
                  const mm = d.getMinutes().toString().padStart(2, "0");
                  return `${hh}:${mm}`;
                })()
              : slot?.endTime || ""),
        },
      ],
    };
    try {
      await ApiService.createAppointment(payload);
      setShowBookingModal(false);
      setSelectedSlot(null);

      await showAlert({
        title: "Randevu Oluşturuldu!",
        message: "Randevunuz başarıyla oluşturuldu. Onay durumunu Randevularım bölümünden takip edebilirsiniz.",
        type: "success",
      });
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Randevu oluşturulurken bir hata oluştu.",
        type: "error",
      });
    }
  };

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col h-full">
        {canAddAppointment ? (
          <DynamicList
            embedded
            type="list"
            header={false}
            title="Randevu Al"
            isSearch={true}
            isPagination={true}
            path=""
            fetcher={fetchProviders}
            emptyMessage="Henüz öğretmen bulunmuyor"
            item={renderProviderItem}
          />
        ) : (
          <div className="h-full bg-gray-50 overflow-hidden flex">
            <div className="flex-1 min-w-0 flex items-center justify-center">
              <div className="text-center text-gray-600">Bu alana erişim yetkiniz bulunmuyor.</div>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={showCalendarModal && !!selectedProvider}
        onClose={() => {
          setShowCalendarModal(false);
          setSelectedProvider(null);
        }}
        size="xl"
        title={selectedProvider ? `${selectedProvider.name} - Randevu Takvimi` : "Randevu Takvimi"}>
        {selectedProvider && (
          <>
            <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-gray-200">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm overflow-hidden">
                {selectedProvider.avatar ? (
                  <img src={selectedProvider.avatar} alt={selectedProvider.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-sm font-medium">
                    {selectedProvider.name
                      .split(" ")
                      .map((n) => n.charAt(0))
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{selectedProvider.name}</h3>
                <p className="text-gray-600 text-xs">{selectedProvider.department}</p>
              </div>
            </div>

            <div className="overflow-x-auto -mx-4 sm:-mx-6 px-4 sm:px-6">
              <WeeklyAvailabilityGrid
                days={days}
                timeSlots={mockTimeSlots}
                availability={computedAvailability}
                selectedEntityId={selectedProvider.id}
                getEntityId={(item) => item.teacherId}
                getIsAvailable={(item) => item.isAvailable}
                getDay={(item) => item.day}
                getTimeSlotId={(item) => item.timeSlotId}
                getPartialAvailability={(day, timeSlotId) => checkPartialAvailability(day, timeSlotId)}
                onToggle={handleSlotClick}
                onCellClick={handleSlotClick}
                disabled={false}
                showLegend={true}
                readOnly={true}
                className="min-w-max"
              />
            </div>
          </>
        )}
      </Modal>
      <TimeSlotDetailModal
        isOpen={showTimeSlotModal}
        onClose={() => setShowTimeSlotModal(false)}
        day={selectedDayForModal}
        timeSlot={selectedTimeSlotForModal || { period: "", startTime: "", endTime: "" }}
        subTimeSlots={getSubTimeSlotsForModal()}
        onToggleSubSlot={handleSubSlotSelect}
        disabled={false}
        readOnly={true}
      />

      <Modal
        isOpen={showBookingModal && !!selectedSlot && !!selectedProvider}
        onClose={() => {
          setShowBookingModal(false);
          setSelectedSlot(null);
        }}
        title="Randevu Oluştur"
        size="sm">
        {selectedSlot && selectedProvider && (
          <>
            <div className="flex items-center space-x-2 mb-4">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">Randevu detaylarını girin</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-gray-700 text-sm font-medium mb-1 flex items-center space-x-2">
                  <User size={14} />
                  <span>Eğitmen</span>
                </label>
                <input
                  type="text"
                  value={selectedProvider.name}
                  disabled
                  className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-gray-900 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-700 text-sm font-medium mb-1 flex items-center space-x-2">
                    <Calendar size={14} />
                    <span>Gün</span>
                  </label>
                  <input
                    type="text"
                    value={selectedSlot.day}
                    disabled
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-gray-900 text-sm"
                  />
                </div>
                <div>
                  <label className="text-gray-700 text-sm font-medium mb-1 flex items-center space-x-2">
                    <Clock size={14} />
                    <span>Saat</span>
                  </label>
                  <input
                    type="text"
                    value={
                      selectedSlot.subTime
                        ? selectedSlot.subTime
                        : `${mockTimeSlots.find((s) => s.id === selectedSlot.timeSlotId)?.startTime} - ${
                            mockTimeSlots.find((s) => s.id === selectedSlot.timeSlotId)?.endTime
                          }`
                    }
                    disabled
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-gray-900 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-4 pt-3 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedSlot(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-200 font-medium text-sm">
                İptal
              </button>
              <button
                onClick={handleBookAppointment}
                className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-200 font-medium flex items-center justify-center space-x-2 shadow-lg text-sm">
                <Send size={16} />
                <span>Randevu Al</span>
              </button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
